import { useState, useEffect } from "react";
import { getLedgerReport } from "../../../services/panel/panelServices";
import toast from "../../../utils/toast";
import Pagination from "../../component/Pagination";

const MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

// <input type="date"> gives "2026-10-29"; the API wants "29 Oct 2026"
const toApiDate = (value) => {
  if (!value) return "";
  const [y, m, d] = value.split("-");
  return `${d} ${MONTHS[Number(m) - 1]} ${y}`;
};

// No dates picked by default — the user chooses them
const defaultFilters = () => ({
  fromDate: "",
  toDate: "",
  accountNo: "",
  transactionId: "",
});

const REPORT_TOP_ROWS = 50;
const TABLE_COLS = 9;
const PAGE_SIZE = 10;

const formatAmount = (amt) => `₹${Number(amt || 0).toLocaleString("en-IN")}`;

// "30 Oct 2026  4:38:11 PM" → "30 Oct 2026, 4:38 PM" (the API pads with a
// double space, so split on any whitespace)
const formatEntryDate = (value) => {
  const [d, m, y, time, ampm] = String(value || "")
    .split(" ")
    .filter(Boolean);
  if (!time) return value || "—";
  return `${d} ${m} ${y}, ${time.split(":").slice(0, 2).join(":")} ${
    ampm || ""
  }`.trim();
};

export default function Ledger() {
  const [isLedgerData, setLedgerData] = useState([]);
  const [ledgerLoading, setLedgerLoading] = useState(false);
  // Chrome fills the saved login mobile into text filters; it never fills a
  // readOnly input, so unlock on focus
  const [filterTextUnlocked, setFilterTextUnlocked] = useState(false);
  const [filters, setFilters] = useState(defaultFilters);
  // API has no paging (TopRows only), so page the returned rows here
  const [pageNumber, setPageNumber] = useState(1);
  const pageStart = (pageNumber - 1) * PAGE_SIZE;
  const pageRows = isLedgerData.slice(pageStart, pageStart + PAGE_SIZE);

  useEffect(() => {
    fetchLedgerReport(defaultFilters());
  }, []);

  const fetchLedgerReport = async (f) => {
    setLedgerLoading(true);
    try {
      const payload = {
        FromDate: toApiDate(f.fromDate),
        ToDate: toApiDate(f.toDate),
        // Static for now — wallet type list not wired yet
        WalletTypeID: 1,
        AccountNo: f.accountNo.trim(),
        TransactionID: f.transactionId.trim(),
        Status: 0,
        TopRows: REPORT_TOP_ROWS,
      };
      const res = await getLedgerReport(payload);

      if (res?.statuscode === 1) {
        setLedgerData(res?.ledgerReport || []);
      } else {
        setLedgerData([]);
      }
      setPageNumber(1);
    } catch (err) {
      console.log(err.response?.data?.msg || "Failed to fetch ledger", "error");
    } finally {
      setLedgerLoading(false);
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((f) => ({ ...f, [name]: value }));
  };

  const handleSearch = () => {
    const { fromDate, toDate } = filters;
    if (fromDate && toDate && fromDate > toDate) {
      toast.error("From date can't be after To date.");
      return;
    }
    fetchLedgerReport(filters);
  };

  const handleReset = () => {
    const defaults = defaultFilters();
    setFilters(defaults);
    fetchLedgerReport(defaults);
  };

  return (
    <>
      <div className="screen active" id="screen-ledger">
        <div className="page-head">
          <div>
            <h1>Ledger</h1>
            <div className="ph-sub">
              Every balance movement, fully auditable
            </div>
          </div>
        </div>
        <div className="card">
          <div className="form-grid form-grid-4">
            <div className="field">
              <label>From date</label>
              <input
                type="date"
                name="fromDate"
                value={filters.fromDate}
                onChange={handleFilterChange}
              />
            </div>
            <div className="field">
              <label>To date</label>
              <input
                type="date"
                name="toDate"
                value={filters.toDate}
                onChange={handleFilterChange}
              />
            </div>
            <div className="field">
              <label>Transaction ID</label>
              <input
                name="transactionId"
                value={filters.transactionId}
                onChange={handleFilterChange}
                placeholder="Enter transaction ID"
                autoComplete="off"
                readOnly={!filterTextUnlocked}
                onFocus={() => setFilterTextUnlocked(true)}
              />
            </div>
            <div className="field">
              <label>Account no</label>
              <input
                name="accountNo"
                value={filters.accountNo}
                onChange={handleFilterChange}
                placeholder="Consumer / account number"
                autoComplete="off"
                readOnly={!filterTextUnlocked}
                onFocus={() => setFilterTextUnlocked(true)}
              />
            </div>
            <div
              className="field"
              style={{ display: "flex", alignItems: "flex-end", gap: 10 }}
            >
              <button
                type="button"
                className="btn btn-ghost"
                onClick={handleReset}
                disabled={ledgerLoading}
              >
                Reset
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleSearch}
                disabled={ledgerLoading}
              >
                {ledgerLoading ? "Searching..." : "Search"}
              </button>
            </div>
          </div>
        </div>
        <div className="card" style={{ marginTop: 20 }}>
          <div className="table-wrap table-scroll">
            <table
              className="data-table"
              id="ledger-table"
              style={{ whiteSpace: "nowrap" }}
            >
              <tbody>
                <tr>
                  <th>S.No</th>
                  <th>Transaction ID</th>
                  <th>Description</th>
                  <th className="tright">Old</th>
                  <th className="tright">Debit</th>
                  <th className="tright">Credit</th>
                  <th className="tright">Current</th>
                  <th>Remark</th>
                  <th>Date</th>
                </tr>
                {ledgerLoading ? (
                  <tr>
                    <td colSpan={TABLE_COLS} style={{ textAlign: "center" }}>
                      Loading...
                    </td>
                  </tr>
                ) : isLedgerData.length === 0 ? (
                  <tr>
                    <td colSpan={TABLE_COLS} style={{ textAlign: "center" }}>
                      No ledger entries found.
                    </td>
                  </tr>
                ) : (
                  pageRows.map((row, i) => (
                    <tr key={row.id}>
                      <td style={{ color: "var(--text-faint)" }}>
                        {pageStart + i + 1}
                      </td>
                      <td>{row.tid}</td>
                      <td>{row.description}</td>
                      <td className="tright mono">
                        {formatAmount(row.lastAmount)}
                      </td>
                      <td className="tright mono">
                        {Number(row.debit) ? (
                          <span style={{ color: "var(--red)" }}>
                            {formatAmount(row.debit)}
                          </span>
                        ) : (
                          <span style={{ color: "var(--text-faint)" }}>—</span>
                        )}
                      </td>
                      <td className="tright mono">
                        {Number(row.credit) ? (
                          <span style={{ color: "var(--teal)" }}>
                            {formatAmount(row.credit)}
                          </span>
                        ) : (
                          <span style={{ color: "var(--text-faint)" }}>—</span>
                        )}
                      </td>
                      <td className="tright mono">
                        {formatAmount(row.curentBalance)}
                      </td>
                      <td style={{ color: "var(--text-dim)" }}>
                        {row.remark || "—"}
                      </td>
                      <td style={{ color: "var(--text-dim)" }}>
                        {formatEntryDate(row.entryDate)}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          {!ledgerLoading && (
            <Pagination
              pageNumber={pageNumber}
              pageSize={PAGE_SIZE}
              totalRecords={isLedgerData.length}
              onChange={setPageNumber}
              label="entries"
            />
          )}
        </div>
      </div>
    </>
  );
}
